import { createFileRoute } from '@tanstack/react-router'
import { lazy, Suspense, useMemo } from 'react'
import { useAgentRegistry } from '@/api/queries/agents'
import { resolveAgentSurfaceRouteTabId } from '@/features/agents/definition'
import { agentOutputPanel, getAgentDefinition, getCanvasPlugin } from '@/features/canvas/registry'
import { SelectionModeFrame } from '@/features/canvas/components/SelectionModeFrame'
import { imageStudioEditorTakeover } from '@/features/canvas/plugins/image-studio/imageStudioEditorLocation'
import { ImageStudioDocumentSkeleton } from '@/features/canvas/plugins/image-studio/imageStudioEditorGeometry'

// Keep the input-canvas renderer and edit engine out of the eager route chunk.
const InputOutputCanvas = lazy(() => import('@/features/canvas/InputOutputCanvas'))

// The Image Studio editor (Polotno SDK + Konva behind its own lazy boundary) is
// far heavier than this route's canvas plugins and is only ever reached through
// a deliberate "Edit in Image Studio". Keep it out of the workspace chunk.
const ImageStudioEditor = lazy(() =>
  import('@/features/canvas/plugins/image-studio/ImageStudioDocumentDetail').then(
    (module) => ({ default: module.ImageStudioDocumentDetail }),
  ),
)

export const Route = createFileRoute('/_app/$projectId/$agentId/')({
  component: AgentWorkspace,
})

function AgentWorkspace() {
  const { projectId, agentId } = Route.useParams()
  const search = Route.useSearch()
  const { data: agents } = useAgentRegistry()

  const CanvasPlugin = getCanvasPlugin(agentOutputPanel(agentId, agents), agents ? agentId : undefined)
  const surface = getAgentDefinition(agentId).surface
  const routeTabId = resolveAgentSurfaceRouteTabId(surface, search)

  // `?editor=` opens the Image Studio editor as a STATE of a public source-agent
  // route, keeping any `?tab=`/`?detail=` trail addressable behind it. It
  // REPLACES the canvas rather than layering over it, so nothing that syncs the
  // Quick Ads canvas back into the URL is mounted to strip the param again.
  const editorTakeover = useMemo(
    () => imageStudioEditorTakeover(agentId, search),
    [agentId, search],
  )

  if (editorTakeover) {
    return (
      <div className="relative h-full">
        <Suspense fallback={<ImageStudioDocumentSkeleton />}>
          <ImageStudioEditor
            // Keep the route shell and Polotno store mounted while a prefetched
            // variation replaces `?editor=`. Project changes still reset the
            // editor; document changes hydrate the existing canvas instance.
            key={projectId}
            projectId={projectId}
            documentId={editorTakeover.documentId}
            version={editorTakeover.version}
            sourceTab={editorTakeover.sourceTab}
            host={editorTakeover.host}
          />
        </Suspense>
      </div>
    )
  }

  return (
    <div className="relative h-full">
      <Suspense fallback={<CanvasLoadingFallback />}>
        <InputOutputCanvas
          projectId={projectId}
          agentId={agentId}
          OutputPlugin={CanvasPlugin}
          routeTabId={routeTabId}
          initialSectionHint={search.section ?? null}
        />
      </Suspense>
      <SelectionModeFrame />
    </div>
  )
}

// Neutral, content-free placeholder for the brief lazy-chunk window. Skeleton
// bars here read as fake content in the spot the real screen is about to fill
// (jarring for canvas-first agents like PDP, whose chunk is also prefetched on
// idle so this rarely shows at all). A plain full-size surface just holds the
// space until the plugin paints.
function CanvasLoadingFallback() {
  return <div className="h-full w-full" aria-label="Loading canvas plugin" />
}
